/* ---------- 3. countdowns ---------- */

import { $, $$ } from '../lib/dom';
import { warn } from '../lib/log';
import { calendarDay, parseZoned } from '../lib/time';
import { resolveZone } from '../lib/zone';

const COUNTDOWN_SOURCES: Record<string, string> = {
  'event-start': 'data-event-start',
  'event-end': 'data-event-end',
  'registration-start': 'data-registration-start',
  'registration-end': 'data-registration-end',
};
const UNITS = [
  ['data-days', 86400],
  ['data-hours', 3600],
  ['data-minutes', 60],
  ['data-seconds', 1],
] as const;
const LABEL = '[data-days-label]';
const pad2 = (n: number) => String(n).padStart(2, '0');
/** Calendar days until `deadline` on the event zone's wall clock: "In 8 days" / "In 1 day" / "Today"; past = empty. */
const daysLabel = (deadline: number, now: number, zone: string | null) => {
  const days = calendarDay(deadline, zone) - calendarDay(now, zone);
  return days > 1 ? `In ${days} days` : days === 1 ? 'In 1 day' : days === 0 ? 'Today' : '';
};

interface Timer {
  deadline: number;
  zone: string | null;
  cells: Array<[HTMLElement | null, number]>;
  label: HTMLElement | null;
}

/** data-countdown="event-start" (alias) or any "data-*" attribute; holder is looked up in scope, then page. */
export const countdownDeadline = (widget: Element) => {
  const name = widget.getAttribute('data-countdown')?.trim() ?? '';
  const source = COUNTDOWN_SOURCES[name] ?? name;
  if (!source.startsWith('data-')) return null;
  const scope = widget.closest('[data-countdown-scope]') ?? document;
  const holder = widget.hasAttribute(source) ? widget : ($(`[${source}]`, scope) ?? $(`[${source}]`));
  if (!holder) return null;
  const zone = resolveZone(widget, holder);
  const deadline = parseZoned(holder.getAttribute(source), zone)?.getTime();
  return deadline ? { deadline, zone } : null;
};

/** One widget's timer, or null — a bad value (unknown attribute selector, unreadable date) skips that widget only. */
const timerOf = (widget: HTMLElement): Timer | null => {
  try {
    const found = countdownDeadline(widget);
    return (
      found && {
        ...found,
        cells: UNITS.map(([hook, secs]): [HTMLElement | null, number] => [$(`[${hook}]`, widget), secs]),
        label: widget.matches(LABEL) ? widget : $(LABEL, widget), // the widget itself may be the single text element
      }
    );
  } catch (err) {
    warn('countdown skipped →', widget, err);
    return null;
  }
};

export const runCountdowns = () => {
  const timers = $$('[data-countdown]')
    .map(timerOf)
    .filter((t): t is Timer => !!t);
  if (!timers.length) return;

  const tick = () => {
    const now = Date.now();
    timers.forEach(({ deadline, zone, cells, label }) => {
      let left = Math.max(0, Math.floor((deadline - now) / 1000));
      cells.forEach(([cell, secs]) => {
        if (cell) cell.textContent = pad2(Math.floor(left / secs));
        left %= secs;
      });
      if (label) label.textContent = daysLabel(deadline, now, zone);
    });
    return timers.every((t) => t.deadline <= now);
  };
  const iv = setInterval(() => tick() && clearInterval(iv), 1000);
  tick();
};
